import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import ImageUpload from '../components/ImageUpload';
import {
    createHomeBlock,
    createGallery,
    deleteHomeBlockVideo,
    deleteHomeBlock,
    deleteGallery,
    deleteImage,
    fetchAllGalleriesAdmin,
    fetchHomeBlocksAdmin,
    fetchImages,
    fetchOrders,
    updateHomeBlock,
    updateOrderFulfillmentStatus,
    uploadHomeBlockVideo,
    uploadImage,
    updateGallery,
} from '../services/api';
import { Gallery, HomeBlock, Image, Order } from '../types';
import { buildGalleryPath } from '../utils/slug';
import { getCurrentSiteKey, SITE_CONFIGS, SiteKey, subscribeToSiteChange } from '../services/site';

type ModalProps = {
    title: string;
    isOpen: boolean;
    onClose: () => void;
    children: React.ReactNode;
    className?: string;
};

const Modal: React.FC<ModalProps> = ({ title, isOpen, onClose, children, className }) => {
    useEffect(() => {
        if (!isOpen) return;
        const handleKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                onClose();
            }
        };
        window.addEventListener('keydown', handleKey);
        return () => window.removeEventListener('keydown', handleKey);
    }, [isOpen, onClose]);

    if (!isOpen) return null;
    return (
        <div className="modal-overlay" role="dialog" aria-modal="true" onClick={onClose}>
            <div className={`modal ${className || ''}`.trim()} onClick={(event) => event.stopPropagation()}>
                <div className="modal__header">
                    <h3>{title}</h3>
                    <button type="button" className="modal__close" onClick={onClose} aria-label="Luk">
                        ×
                    </button>
                </div>
                <div className="modal__body">{children}</div>
            </div>
        </div>
    );
};

const Admin: React.FC = () => {
    const [galleries, setGalleries] = useState<Gallery[]>([]);
    const [images, setImages] = useState<Image[]>([]);
    const [homeBlocks, setHomeBlocks] = useState<HomeBlock[]>([]);
    const [orders, setOrders] = useState<Order[]>([]);
    const [error, setError] = useState<string | null>(null);
    const [siteKey, setSiteKey] = useState<SiteKey>(getCurrentSiteKey());
    const location = useLocation();
    const [isCreateGalleryOpen, setIsCreateGalleryOpen] = useState(false);
    const [isUploadOpen, setIsUploadOpen] = useState(false);
    const [editingGallery, setEditingGallery] = useState<Gallery | null>(null);

    const [newTitle, setNewTitle] = useState('');
    const [newDescription, setNewDescription] = useState('');
    const [newIsPublic, setNewIsPublic] = useState(true);
    const [newIsActive, setNewIsActive] = useState(true);
    const [newPassword, setNewPassword] = useState('');

    const [editTitle, setEditTitle] = useState('');
    const [editDescription, setEditDescription] = useState('');
    const [editIsPublic, setEditIsPublic] = useState(true);
    const [editIsActive, setEditIsActive] = useState(true);
    const [editPassword, setEditPassword] = useState('');
    const [clearPassword, setClearPassword] = useState(false);
    const [folderUploadTarget, setFolderUploadTarget] = useState<Gallery | null>(null);
    const [folderUploadProgress, setFolderUploadProgress] = useState<{ current: number; total: number } | null>(null);
    const folderInputRef = useRef<HTMLInputElement | null>(null);

    const [imageSearch, setImageSearch] = useState('');
    const [imageGalleryFilter, setImageGalleryFilter] = useState('all');
    const [imageHomepageFilter, setImageHomepageFilter] = useState('all');
    const [imageVisionFilter, setImageVisionFilter] = useState('all');
    const [imageSort, setImageSort] = useState('newest');

    const [isCreateBlockOpen, setIsCreateBlockOpen] = useState(false);
    const [editingBlock, setEditingBlock] = useState<HomeBlock | null>(null);
    const [newBlockTitle, setNewBlockTitle] = useState('');
    const [newBlockDescription, setNewBlockDescription] = useState('');
    const [newBlockVisible, setNewBlockVisible] = useState(true);
    const [newBlockSortOrder, setNewBlockSortOrder] = useState(100);
    const [newBlockImageIds, setNewBlockImageIds] = useState<number[]>([]);
    const [newBlockVideoFile, setNewBlockVideoFile] = useState<File | null>(null);
    const [removeBlockVideo, setRemoveBlockVideo] = useState(false);


    const currentSite = SITE_CONFIGS[siteKey];

    const loadData = useCallback(async () => {
        try {
            const [galleriesData, imagesData, blocksData, ordersData] = await Promise.all([
                fetchAllGalleriesAdmin(),
                fetchImages(),
                fetchHomeBlocksAdmin(),
                fetchOrders(),
            ]);
            setGalleries(galleriesData);
            setImages(imagesData);
            setHomeBlocks(blocksData || []);
            setOrders(ordersData || []);
        } catch (e) {
            setError('Failed to load admin data');
        }
    }, []);

    useEffect(() => {
        loadData();
    }, [loadData, siteKey]);

    useEffect(() => subscribeToSiteChange((nextSiteKey) => {
        setSiteKey(nextSiteKey);
        setImageGalleryFilter('all');
        setImageSearch('');
        setError(null);
    }), []);

    const handleCreateGallery = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            await createGallery({
                title: newTitle,
                description: newDescription,
                isPublic: newIsPublic,
                isActive: newIsActive,
                password: newPassword || undefined,
            });
            setNewTitle('');
            setNewDescription('');
            setNewPassword('');
            setNewIsPublic(true);
            setNewIsActive(true);
            setIsCreateGalleryOpen(false);
            await loadData();
        } catch (e) {
            setError('Failed to create gallery');
        }
    };

    const openEditGallery = (gallery: Gallery) => {
        setEditingGallery(gallery);
        setEditTitle(gallery.title);
        setEditDescription(gallery.description || '');
        setEditIsPublic(Boolean(gallery.isPublic));
        setEditIsActive(gallery.isActive !== false);
        setEditPassword('');
        setClearPassword(false);
    };

    const handleUpdateGallery = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!editingGallery) return;
        try {
            await updateGallery(editingGallery.id, {
                title: editTitle,
                description: editDescription,
                isPublic: editIsPublic,
                isActive: editIsActive,
                password: clearPassword ? '' : editPassword || undefined,
            });
            setEditingGallery(null);
            await loadData();
        } catch (e) {
            setError('Failed to update gallery');
        }
    };


    const handleDeleteGallery = async (galleryId: string) => {
        if (!window.confirm('Vil du slette galleriet?')) return;
        try {
            await deleteGallery(galleryId);
            await loadData();
        } catch (e) {
            setError('Failed to delete gallery');
        }
    };

    const handleDeleteImage = async (imageId: string) => {
        if (!window.confirm('Vil du slette billedet?')) return;
        try {
            await deleteImage(imageId);
            await loadData();
        } catch (e) {
            setError('Failed to delete image');
        }
    };

    const handleFolderUploadClick = (gallery: Gallery) => {
        setFolderUploadTarget(gallery);
        if (folderInputRef.current) {
            folderInputRef.current.value = '';
            folderInputRef.current.click();
        }
    };

    const handleFolderUploadChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        if (!folderUploadTarget) return;
        const files = event.target.files ? Array.from(event.target.files) : [];
        if (!files.length) return;

        const isImageFile = (file: File) => {
            if (file.type && file.type.startsWith('image/')) return true;
            const name = file.name.toLowerCase();
            return ['.jpg', '.jpeg', '.png', '.gif', '.webp'].some((ext) => name.endsWith(ext));
        };

        const imageFiles = files.filter(isImageFile);
        if (!imageFiles.length) {
            setError('Ingen gyldige billedfiler i mappen.');
            return;
        }

        setFolderUploadProgress({ current: 0, total: imageFiles.length });
        try {
            for (let index = 0; index < imageFiles.length; index += 1) {
                const file = imageFiles[index];
                const formData = new FormData();
                formData.append('image', file);
                formData.append('galleryId', folderUploadTarget.id);
                await uploadImage(formData);
                setFolderUploadProgress({ current: index + 1, total: imageFiles.length });
            }
            await loadData();
        } catch (e) {
            // @ts-ignore
            const message = e && e.response && e.response.data && e.response.data.message;
            setError(message || 'Fejl ved folder-upload.');
        } finally {
            setFolderUploadProgress(null);
        }
    };


    const galleryCount = galleries.length;
    const publicGalleryCount = galleries.filter((g) => g.isPublic).length;
    const protectedGalleryCount = galleries.filter((g) => !g.isPublic || g.isPasswordProtected).length;
    const imageCount = images.length;
    const unassignedImageCount = images.filter((img) => !img.galleryId).length;
    const paidOrderCount = orders.filter((order) => order.status === 'paid').length;
    const pendingFulfillmentCount = orders.filter((order) => order.status === 'paid' && order.fulfillmentStatus !== 'fulfilled').length;
    const galleryTitleById = useMemo(() => {
        return galleries.reduce<Record<string, string>>((acc, g) => {
            acc[String(g.id)] = g.title;
            return acc;
        }, {});
    }, [galleries]);

    const filteredImages = useMemo(() => {
        const searchValue = imageSearch.trim().toLowerCase();
        let result = images.filter((img) => {
            if (!searchValue) return true;
            const title = img.title?.toLowerCase() || '';
            const description = img.description?.toLowerCase() || '';
            const filename = img.filename?.toLowerCase() || '';
            return (
                title.includes(searchValue) ||
                description.includes(searchValue) ||
                filename.includes(searchValue)
            );
        });

        if (imageGalleryFilter !== 'all') {
            if (imageGalleryFilter === 'none') {
                result = result.filter((img) => !img.galleryId);
            } else {
                result = result.filter((img) => String(img.galleryId) === imageGalleryFilter);
            }
        }

        if (imageHomepageFilter !== 'all') {
            const shouldBeOnHomepage = imageHomepageFilter === 'yes';
            result = result.filter((img) => Boolean(img.showOnHomepage) === shouldBeOnHomepage);
        }

        if (imageVisionFilter !== 'all') {
            const shouldBeVision = imageVisionFilter === 'yes';
            result = result.filter((img) => Boolean(img.showAsVision) === shouldBeVision);
        }

        const galleryTitle = (img: Image) => {
            if (!img.galleryId) return '';
            return galleryTitleById[String(img.galleryId)] || '';
        };

        const sortHandlers: Record<string, (a: Image, b: Image) => number> = {
            newest: (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
            oldest: (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
            title: (a, b) => (a.title || '').localeCompare(b.title || ''),
            gallery: (a, b) => galleryTitle(a).localeCompare(galleryTitle(b)),
        };

        const sorter = sortHandlers[imageSort] || sortHandlers.newest;
        return [...result].sort(sorter);
    }, [images, imageSearch, imageGalleryFilter, imageHomepageFilter, imageVisionFilter, imageSort, galleryTitleById]);

    const latestUploadedImages = useMemo(() => {
        return [...images]
            .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
            .slice(0, 20);
    }, [images]);

    const activeSection = location.hash;
    const showDashboard = !activeSection || activeSection === '#dashboard';
    const showGalleries = activeSection === '#galleries';
    const showImages = activeSection === '#images';
    const showHomeBlocks = activeSection === '#home-blocks';
    const showOrders = activeSection === '#orders';

    const formatOrderAmount = (order: Order) => {
        const formatter = new Intl.NumberFormat('da-DK', {
            style: 'currency',
            currency: order.currency || 'dkk',
            minimumFractionDigits: order.amountTotalCents % 100 === 0 ? 0 : 2,
        });
        return formatter.format(order.amountTotalCents / 100);
    };

    const handleUpdateFulfillmentStatus = async (orderId: number, fulfillmentStatus: string) => {
        try {
            await updateOrderFulfillmentStatus(orderId, fulfillmentStatus);
            await loadData();
        } catch (e) {
            setError('Failed to update order');
        }
    };

    const toggleBlockImage = (imageId: number, checked: boolean, currentIds: number[]) => {
        if (checked) {
            return Array.from(new Set([...currentIds, imageId]));
        }
        return currentIds.filter((id) => id !== imageId);
    };

    const resetBlockForm = () => {
        setNewBlockTitle('');
        setNewBlockDescription('');
        setNewBlockVisible(true);
        setNewBlockSortOrder(100);
        setNewBlockImageIds([]);
        setNewBlockVideoFile(null);
        setRemoveBlockVideo(false);
    };

    const handleCreateBlock = async (event: React.FormEvent) => {
        event.preventDefault();
        try {
            const createdBlock = await createHomeBlock({
                title: newBlockTitle,
                description: newBlockDescription,
                isVisible: newBlockVisible,
                sortOrder: newBlockSortOrder,
                imageIds: newBlockImageIds,
            });
            if (newBlockVideoFile && createdBlock?.id) {
                await uploadHomeBlockVideo(createdBlock.id, newBlockVideoFile);
            }
            resetBlockForm();
            setIsCreateBlockOpen(false);
            await loadData();
        } catch (e) {
            setError('Failed to create home block');
        }
    };

    const openCreateBlockModal = () => {
        resetBlockForm();
        setIsCreateBlockOpen(true);
    };

    const openEditBlockModal = (block: HomeBlock) => {
        setEditingBlock(block);
        setNewBlockTitle(block.title || '');
        setNewBlockDescription(block.description || '');
        setNewBlockVisible(Boolean(block.isVisible));
        setNewBlockSortOrder(Number.isFinite(Number(block.sortOrder)) ? Number(block.sortOrder) : 100);
        setNewBlockImageIds(
            Array.isArray(block.imageIds)
                ? block.imageIds.map((id) => Number(id)).filter((id) => Number.isFinite(id))
                : (block.images || []).map((img) => Number(img.id)).filter((id) => Number.isFinite(id))
        );
        setNewBlockVideoFile(null);
        setRemoveBlockVideo(false);
    };

    const handleSaveBlock = async (event: React.FormEvent) => {
        event.preventDefault();
        if (!editingBlock) return;
        try {
            await updateHomeBlock(editingBlock.id, {
                title: newBlockTitle,
                description: newBlockDescription,
                isVisible: newBlockVisible,
                sortOrder: newBlockSortOrder,
                imageIds: newBlockImageIds,
            });
            if (removeBlockVideo && !newBlockVideoFile) {
                await deleteHomeBlockVideo(editingBlock.id);
            }
            if (newBlockVideoFile) {
                await uploadHomeBlockVideo(editingBlock.id, newBlockVideoFile);
            }
            setEditingBlock(null);
            resetBlockForm();
            await loadData();
        } catch (e) {
            setError('Failed to save home block');
        }
    };

    const handleDeleteBlock = async (block: HomeBlock) => {
        if (block.isSystem) {
            setError('Systemblokke kan ikke slettes.');
            return;
        }
        if (!window.confirm('Vil du slette denne blok?')) return;
        try {
            await deleteHomeBlock(block.id);
            await loadData();
        } catch (e) {
            setError('Failed to delete home block');
        }
    };

    return (
        <div>
            <h1>{showHomeBlocks ? 'Forsideblokke' : 'Dashboard'}</h1>
            <p className="gallery-list-meta">Aktivt site: {currentSite.name}</p>
            {error && <div className="error">{error}</div>}

            <input
                ref={folderInputRef}
                type="file"
                multiple
                // @ts-ignore
                webkitdirectory=""
                style={{ display: 'none' }}
                onChange={handleFolderUploadChange}
            />

            {showDashboard && (
                <>
                    <div className="admin-actions">
                        <button type="button" onClick={() => setIsCreateGalleryOpen(true)}>
                            Opret galleri
                        </button>
                        <button type="button" onClick={() => setIsUploadOpen(true)}>
                            Upload billede
                        </button>
                    </div>

                    <section className="admin-dashboard">
                        <div className="admin-card">
                            <span className="admin-card__label">Gallerier i alt</span>
                            <strong>{galleryCount}</strong>
                        </div>
                        <div className="admin-card">
                            <span className="admin-card__label">Offentlige gallerier</span>
                            <strong>{publicGalleryCount}</strong>
                        </div>
                        <div className="admin-card">
                            <span className="admin-card__label">Beskyttede gallerier</span>
                            <strong>{protectedGalleryCount}</strong>
                        </div>
                        <div className="admin-card">
                            <span className="admin-card__label">Billeder i alt</span>
                            <strong>{imageCount}</strong>
                        </div>
                        <div className="admin-card">
                            <span className="admin-card__label">Uden galleri</span>
                            <strong>{unassignedImageCount}</strong>
                        </div>
                        <div className="admin-card">
                            <span className="admin-card__label">Betalte ordrer</span>
                            <strong>{paidOrderCount}</strong>
                        </div>
                        <div className="admin-card">
                            <span className="admin-card__label">Afventer opfyldelse</span>
                            <strong>{pendingFulfillmentCount}</strong>
                        </div>
                    </section>

                    <section className="admin-section">
                        <h2>Seneste uploads</h2>
                        <ul className="admin-image-list">
                            {latestUploadedImages.map((img) => (
                                <li key={`dashboard-${img.id}`} className="admin-image-item">
                                    <img src={img.thumbnailUrl} alt={img.title || 'billede'} />
                                    <div className="admin-row admin-row--image">
                                        <span className="admin-row__title">{img.title || 'Uden titel'}</span>
                                        <span className="admin-row__meta admin-row__truncate">
                                            {img.description || '—'}
                                        </span>
                                        <span className="admin-row__meta admin-row__truncate">
                                            {img.filename ? `Fil: ${img.filename}` : 'Fil: —'}
                                        </span>
                                        <span className="admin-row__meta admin-row__truncate">
                                            {img.galleryId ? galleryTitleById[img.galleryId] || 'Ukendt galleri' : 'Ingen galleri'}
                                        </span>
                                        <span className="admin-row__meta">
                                            Forside: {img.showOnHomepage ? 'Ja' : 'Nej'}
                                        </span>
                                        <span className="admin-row__meta">
                                            Vision: {img.showAsVision ? 'Ja' : 'Nej'}
                                        </span>
                                        <span className="admin-row__meta">
                                            Oprettet {new Date(img.createdAt).toLocaleDateString('da-DK')}
                                        </span>
                                        <div className="admin-list-actions">
                                            <a className="link-button" href={`/admin/images/${img.id}`}>
                                                Rediger
                                            </a>
                                            <button type="button" className="link-button link-button--danger" onClick={() => handleDeleteImage(img.id)}>
                                                Slet
                                            </button>
                                        </div>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </section>
                </>
            )}

            <section
                id="home-blocks"
                className={`admin-section admin-section--list ${showHomeBlocks ? 'is-active' : ''}`}
            >
                <div className="admin-actions">
                    <button type="button" onClick={openCreateBlockModal}>Opret ny blok</button>
                </div>

                <ul className="admin-home-block-list">
                    {homeBlocks.map((block) => (
                        <li key={block.id} className="admin-home-block-item admin-home-block-item--compact">
                            <div className="admin-home-block-item__head">
                                <strong>{block.title || block.key}</strong>
                                <div className="admin-home-block-item__head-right">
                                    <span className="admin-home-block-item__order">#{block.sortOrder}</span>
                                    <span className="gallery-list-meta">{block.isSystem ? 'Systemblok' : 'Custom blok'}</span>
                                </div>
                            </div>
                            <div className="admin-home-block-item__meta">
                                <span className="admin-row__meta">Key: {block.key}</span>
                                <span className="admin-home-block-item__sep" aria-hidden="true">•</span>
                                <span className="admin-row__meta">Synlig: {block.isVisible ? 'Ja' : 'Nej'}</span>
                                <span className="admin-home-block-item__sep" aria-hidden="true">•</span>
                                <span className="admin-row__meta">Sortering: {block.sortOrder}</span>
                                <span className="admin-home-block-item__sep" aria-hidden="true">•</span>
                                <span className="admin-row__meta">Billeder: {(block.imageIds || block.images || []).length}</span>
                                <span className="admin-home-block-item__sep" aria-hidden="true">•</span>
                                <span className="admin-row__meta">Video: {block.videoUrl ? 'Ja' : 'Nej'}</span>
                            </div>
                            {block.description ? <p className="gallery-list-meta">{block.description}</p> : null}
                            <div className="admin-list-actions">
                                <button type="button" className="link-button" onClick={() => openEditBlockModal(block)}>
                                    Rediger
                                </button>
                                <button
                                    type="button"
                                    className="link-button link-button--danger"
                                    onClick={() => handleDeleteBlock(block)}
                                    disabled={block.isSystem}
                                >
                                    Slet
                                </button>
                            </div>
                        </li>
                    ))}
                </ul>
            </section>

            <section
                id="orders"
                className={`admin-section admin-section--list ${showOrders ? 'is-active' : ''}`}
            >
                <h2>Ordrer</h2>
                {!orders.length ? (
                    <p className="gallery-list-meta">Ingen ordrer endnu.</p>
                ) : (
                    <ul className="admin-order-list">
                        {orders.map((order) => {
                            const orderItems = order.items && order.items.length
                                ? order.items
                                : [{
                                    id: order.id,
                                    imageId: order.imageId,
                                    printOptionId: order.printOptionId,
                                    quantity: 1,
                                    amountTotalCents: order.amountTotalCents,
                                    currency: order.currency,
                                    image: order.image,
                                    printOption: order.printOption,
                                    metadata: order.metadata,
                                }];

                            return (
                                <li key={order.id} className="admin-order-item">
                                    <div className="admin-row admin-row--order">
                                        <span className="admin-row__title">
                                            #{order.id} · {orderItems.length > 1 ? `${orderItems.length} print` : (order.image?.title || order.metadata?.imageTitle || 'Printordre')}
                                        </span>
                                        <span className="admin-row__meta">
                                            {formatOrderAmount(order)}
                                        </span>
                                        <div className="admin-order-item__lines">
                                            {orderItems.map((item) => (
                                                <span key={`${order.id}-${item.id}-${item.printOptionId || 'print'}`} className="admin-row__meta">
                                                    {item.quantity} x {item.image?.title || item.metadata?.imageTitle || 'Print'} · {item.printOption?.label || item.metadata?.printLabel || 'Print'}
                                                </span>
                                            ))}
                                        </div>
                                        <span className="admin-row__meta">
                                            Betaling: {order.status}
                                        </span>
                                        <span className="admin-row__meta">
                                            Kunde: {order.customerName || order.customerEmail || '—'}
                                        </span>
                                        <span className="admin-row__meta">
                                            Oprettet {new Date(order.createdAt).toLocaleDateString('da-DK')}
                                        </span>
                                        <div className="admin-list-actions admin-list-actions--order">
                                            <select
                                                value={order.fulfillmentStatus}
                                                onChange={(event) => handleUpdateFulfillmentStatus(order.id, event.target.value)}
                                            >
                                                <option value="pending">Afventer</option>
                                                <option value="processing">Behandles</option>
                                                <option value="fulfilled">Afsendt / opfyldt</option>
                                                <option value="cancelled">Annulleret</option>
                                            </select>
                                            {order.stripeCheckoutSessionId && (
                                                <span className="admin-row__meta admin-row__truncate">
                                                    Stripe: {order.stripeCheckoutSessionId}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                )}
            </section>

            <section
                id="galleries"
                className={`admin-section admin-section--list ${showGalleries ? 'is-active' : ''}`}
            >
                <h2>Galleriliste</h2>
                <ul className="gallery-list">
                    {galleries.map((g) => (
                        <li key={g.id} className="gallery-list-item">
                            <div className="admin-row">
                                <a className="admin-row__title" href={`/admin/galleries/${g.id}`}>
                                    {g.title}
                                </a>
                                <span className="admin-row__meta admin-row__truncate">
                                    {g.description || '—'}
                                </span>
                                <span className="admin-row__meta">
                                    {g.images?.length || 0} billeder
                                </span>
                                <span className="admin-row__meta">
                                    Oprettet {new Date(g.createdAt).toLocaleDateString('da-DK')}
                                </span>
                                <span className="admin-row__meta">
                                    {g.isPublic ? 'Offentlig' : 'Beskyttet'} {g.isPasswordProtected ? '(kode)' : ''}
                                </span>
                                <span className="admin-row__meta">
                                    {g.isActive === false ? 'Inaktiv' : 'Aktiv'}
                                </span>
                                <div className="admin-list-actions">
                                    <button type="button" className="link-button" onClick={() => openEditGallery(g)}>
                                        Rediger
                                    </button>
                                    <a className="link-button" href={`/admin/galleries/${g.id}`}>
                                        Rediger indhold
                                    </a>
                                    <a className="link-button" href={buildGalleryPath(g)}>
                                        Vis offentlig
                                    </a>
                                    <button type="button" className="link-button" onClick={() => handleFolderUploadClick(g)}>
                                        Upload mappe
                                    </button>
                                    <button type="button" className="link-button link-button--danger" onClick={() => handleDeleteGallery(g.id)}>
                                        Slet
                                    </button>
                                </div>
                            </div>
                            {folderUploadTarget?.id === g.id && folderUploadProgress && (
                                <div className="gallery-list-meta">
                                    Uploader {folderUploadProgress.current}/{folderUploadProgress.total}
                                </div>
                            )}
                        </li>
                    ))}
                </ul>
            </section>

            <section
                id="images"
                className={`admin-section admin-section--list ${showImages ? 'is-active' : ''}`}
            >
                <h2>Billedliste</h2>
                <div className="admin-filters">
                    <div className="admin-filters__group">
                        <label className="admin-filters__label" htmlFor="image-search">
                            Søg
                        </label>
                        <input
                            id="image-search"
                            className="admin-filters__input"
                            type="text"
                            placeholder="Titel, filnavn eller beskrivelse"
                            value={imageSearch}
                            onChange={(event) => setImageSearch(event.target.value)}
                        />
                    </div>
                    <div className="admin-filters__group">
                        <label className="admin-filters__label" htmlFor="image-gallery-filter">
                            Galleri
                        </label>
                        <select
                            id="image-gallery-filter"
                            className="admin-filters__select"
                            value={imageGalleryFilter}
                            onChange={(event) => setImageGalleryFilter(event.target.value)}
                        >
                            <option value="all">Alle</option>
                            <option value="none">Ingen galleri</option>
                            {galleries
                                .slice()
                                .sort((a, b) => a.title.localeCompare(b.title))
                                .map((gallery) => (
                                    <option key={gallery.id} value={gallery.id}>
                                        {gallery.title}
                                    </option>
                                ))}
                        </select>
                    </div>
                    <div className="admin-filters__group">
                        <label className="admin-filters__label" htmlFor="image-homepage-filter">
                            Forside
                        </label>
                        <select
                            id="image-homepage-filter"
                            className="admin-filters__select"
                            value={imageHomepageFilter}
                            onChange={(event) => setImageHomepageFilter(event.target.value)}
                        >
                            <option value="all">Alle</option>
                            <option value="yes">Ja</option>
                            <option value="no">Nej</option>
                        </select>
                    </div>
                    <div className="admin-filters__group">
                        <label className="admin-filters__label" htmlFor="image-vision-filter">
                            Vision
                        </label>
                        <select
                            id="image-vision-filter"
                            className="admin-filters__select"
                            value={imageVisionFilter}
                            onChange={(event) => setImageVisionFilter(event.target.value)}
                        >
                            <option value="all">Vision: Alle</option>
                            <option value="yes">Vision: Ja</option>
                            <option value="no">Vision: Nej</option>
                        </select>
                    </div>
                    <div className="admin-filters__group">
                        <label className="admin-filters__label" htmlFor="image-sort">
                            Sorter
                        </label>
                        <select
                            id="image-sort"
                            className="admin-filters__select"
                            value={imageSort}
                            onChange={(event) => setImageSort(event.target.value)}
                        >
                            <option value="newest">Nyeste først</option>
                            <option value="oldest">Ældste først</option>
                            <option value="title">Titel (A-Å)</option>
                            <option value="gallery">Galleri (A-Å)</option>
                        </select>
                    </div>
                    <div className="admin-filters__actions">
                        <span className="admin-filters__count">
                            Viser {filteredImages.length} af {images.length}
                        </span>
                    </div>
                </div>
                <ul className="admin-image-list">
                    {filteredImages.map((img) => (
                        <li key={img.id} className="admin-image-item">
                            <img src={img.thumbnailUrl} alt={img.title || 'billede'} />
                            <div className="admin-row admin-row--image">
                                <span className="admin-row__title">{img.title || 'Uden titel'}</span>
                                <span className="admin-row__meta admin-row__truncate">
                                    {img.description || '—'}
                                </span>
                                <span className="admin-row__meta admin-row__truncate">
                                    {img.filename ? `Fil: ${img.filename}` : 'Fil: —'}
                                </span>
                                <span className="admin-row__meta admin-row__truncate">
                                    {img.galleryId ? galleryTitleById[img.galleryId] || 'Ukendt galleri' : 'Ingen galleri'}
                                </span>
                                <span className="admin-row__meta">
                                    Forside: {img.showOnHomepage ? 'Ja' : 'Nej'}
                                </span>
                                <span className="admin-row__meta">
                                    Vision: {img.showAsVision ? 'Ja' : 'Nej'}
                                </span>
                                <span className="admin-row__meta">
                                    Oprettet {new Date(img.createdAt).toLocaleDateString('da-DK')}
                                </span>
                                <div className="admin-list-actions">
                                    <a className="link-button" href={`/admin/images/${img.id}`}>
                                        Rediger
                                    </a>
                                    <button type="button" className="link-button link-button--danger" onClick={() => handleDeleteImage(img.id)}>
                                        Slet
                                    </button>
                                </div>
                            </div>
                        </li>
                    ))}
                </ul>
            </section>

            <Modal title="Opret galleri" isOpen={isCreateGalleryOpen} onClose={() => setIsCreateGalleryOpen(false)}>
                <form onSubmit={handleCreateGallery} className="modal-form">
                    <label>
                        Titel
                        <input
                            type="text"
                            value={newTitle}
                            onChange={(e) => setNewTitle(e.target.value)}
                            required
                        />
                    </label>
                    <label>
                        Beskrivelse
                        <input
                            type="text"
                            value={newDescription}
                            onChange={(e) => setNewDescription(e.target.value)}
                        />
                    </label>
                    <label className="checkbox-row">
                        <input
                            type="checkbox"
                            checked={newIsPublic}
                            onChange={(e) => setNewIsPublic(e.target.checked)}
                        />
                        Offentlig
                    </label>
                    <label className="checkbox-row">
                        <input
                            type="checkbox"
                            checked={newIsActive}
                            onChange={(e) => setNewIsActive(e.target.checked)}
                        />
                        Aktiv
                    </label>
                    <label>
                        Adgangskode (valgfri)
                        <input
                            type="text"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                        />
                    </label>
                    <div className="modal-actions">
                        <button type="button" onClick={() => setIsCreateGalleryOpen(false)}>
                            Annuller
                        </button>
                        <button type="submit">Opret</button>
                    </div>
                </form>
            </Modal>

            <Modal title="Upload billede" isOpen={isUploadOpen} onClose={() => setIsUploadOpen(false)}>
                <ImageUpload
                    onUploaded={async () => {
                        setIsUploadOpen(false);
                        await loadData();
                    }}
                />
            </Modal>

            <Modal title="Rediger galleri" isOpen={Boolean(editingGallery)} onClose={() => setEditingGallery(null)}>
                <form onSubmit={handleUpdateGallery} className="modal-form">
                    <label>
                        Titel
                        <input
                            type="text"
                            value={editTitle}
                            onChange={(e) => setEditTitle(e.target.value)}
                            required
                        />
                    </label>
                    <label>
                        Beskrivelse
                        <input
                            type="text"
                            value={editDescription}
                            onChange={(e) => setEditDescription(e.target.value)}
                        />
                    </label>
                    <label className="checkbox-row">
                        <input
                            type="checkbox"
                            checked={editIsPublic}
                            onChange={(e) => setEditIsPublic(e.target.checked)}
                        />
                        Offentlig
                    </label>
                    <label className="checkbox-row">
                        <input
                            type="checkbox"
                            checked={editIsActive}
                            onChange={(e) => setEditIsActive(e.target.checked)}
                        />
                        Aktiv
                    </label>
                    <label>
                        Ny adgangskode (valgfri)
                        <input
                            type="text"
                            value={editPassword}
                            onChange={(e) => setEditPassword(e.target.value)}
                        />
                    </label>
                    <label className="checkbox-row">
                        <input
                            type="checkbox"
                            checked={clearPassword}
                            onChange={(e) => setClearPassword(e.target.checked)}
                        />
                        Fjern adgangskode
                    </label>
                    {editingGallery && (
                        <div className="gallery-edit-content-link">
                            <span>Blokke, video og tekst</span>
                            <a className="link-button" href={`/admin/galleries/${editingGallery.id}`}>
                                Rediger indhold
                            </a>
                        </div>
                    )}
                    <div className="modal-actions">
                        <button type="button" onClick={() => setEditingGallery(null)}>
                            Annuller
                        </button>
                        <button type="submit">Gem</button>
                    </div>
                </form>
            </Modal>

            <Modal
                title="Opret forsideblok"
                isOpen={isCreateBlockOpen}
                onClose={() => {
                    setIsCreateBlockOpen(false);
                    resetBlockForm();
                }}
                className="modal--wide"
            >
                <form onSubmit={handleCreateBlock} className="modal-form admin-home-block-form">
                    <div className="admin-home-block-form__main">
                        <label>
                            Titel
                            <input
                                type="text"
                                value={newBlockTitle}
                                onChange={(event) => setNewBlockTitle(event.target.value)}
                                required
                            />
                        </label>
                        <label>
                            Beskrivelse
                            <textarea
                                value={newBlockDescription}
                                onChange={(event) => setNewBlockDescription(event.target.value)}
                            />
                        </label>
                        <label>
                            Sortering
                            <input
                                type="number"
                                value={newBlockSortOrder}
                                onChange={(event) => setNewBlockSortOrder(Number(event.target.value) || 0)}
                            />
                        </label>
                        <label className="checkbox-row">
                            <input
                                type="checkbox"
                                checked={newBlockVisible}
                                onChange={(event) => setNewBlockVisible(event.target.checked)}
                            />
                            Vis blok
                        </label>
                        <div className="admin-home-block-video">
                            <label>
                                Video
                                <input
                                    type="file"
                                    accept="video/mp4,video/webm,video/quicktime,.mp4,.webm,.mov"
                                    onChange={(event) => setNewBlockVideoFile(event.target.files?.[0] || null)}
                                />
                            </label>
                            {newBlockVideoFile ? (
                                <p className="gallery-list-meta">Valgt video: {newBlockVideoFile.name}</p>
                            ) : null}
                        </div>
                    </div>
                    <div className="admin-home-block-images">
                        <p className="gallery-list-meta">Vælg billeder</p>
                        <div className="admin-home-block-images__grid">
                            {images.map((image) => (
                                <label key={`new-${image.id}`} className="checkbox-row">
                                    <input
                                        type="checkbox"
                                        checked={newBlockImageIds.includes(Number(image.id))}
                                        onChange={(event) =>
                                            setNewBlockImageIds((current) =>
                                                toggleBlockImage(Number(image.id), event.target.checked, current)
                                            )
                                        }
                                    />
                                    <span>{image.title || image.filename || `Billede ${image.id}`}</span>
                                </label>
                            ))}
                        </div>
                    </div>
                    <div className="modal-actions admin-home-block-form__actions">
                        <button
                            type="button"
                            onClick={() => {
                                setIsCreateBlockOpen(false);
                                resetBlockForm();
                            }}
                        >
                            Annuller
                        </button>
                        <button type="submit">Opret</button>
                    </div>
                </form>
            </Modal>

            <Modal
                title="Rediger forsideblok"
                isOpen={Boolean(editingBlock)}
                onClose={() => {
                    setEditingBlock(null);
                    resetBlockForm();
                }}
                className="modal--wide"
            >
                <form onSubmit={handleSaveBlock} className="modal-form admin-home-block-form">
                    <div className="admin-home-block-form__main">
                        <label>
                            Titel
                            <input
                                type="text"
                                value={newBlockTitle}
                                onChange={(event) => setNewBlockTitle(event.target.value)}
                                required
                            />
                        </label>
                        <label>
                            Beskrivelse
                            <textarea
                                value={newBlockDescription}
                                onChange={(event) => setNewBlockDescription(event.target.value)}
                            />
                        </label>
                        <label>
                            Sortering
                            <input
                                type="number"
                                value={newBlockSortOrder}
                                onChange={(event) => setNewBlockSortOrder(Number(event.target.value) || 0)}
                            />
                        </label>
                        <label className="checkbox-row">
                            <input
                                type="checkbox"
                                checked={newBlockVisible}
                                onChange={(event) => setNewBlockVisible(event.target.checked)}
                            />
                            Vis blok
                        </label>
                        <div className="admin-home-block-video">
                            {editingBlock?.videoUrl && !removeBlockVideo ? (
                                <video className="admin-home-block-video__preview" src={editingBlock.videoUrl} controls preload="metadata" />
                            ) : null}
                            <label>
                                Video
                                <input
                                    type="file"
                                    accept="video/mp4,video/webm,video/quicktime,.mp4,.webm,.mov"
                                    onChange={(event) => {
                                        setNewBlockVideoFile(event.target.files?.[0] || null);
                                        setRemoveBlockVideo(false);
                                    }}
                                />
                            </label>
                            {newBlockVideoFile ? (
                                <p className="gallery-list-meta">Ny video: {newBlockVideoFile.name}</p>
                            ) : null}
                            {editingBlock?.videoUrl ? (
                                <label className="checkbox-row">
                                    <input
                                        type="checkbox"
                                        checked={removeBlockVideo}
                                        onChange={(event) => {
                                            setRemoveBlockVideo(event.target.checked);
                                            if (event.target.checked) {
                                                setNewBlockVideoFile(null);
                                            }
                                        }}
                                    />
                                    Fjern eksisterende video
                                </label>
                            ) : null}
                        </div>
                    </div>
                    <div className="admin-home-block-images">
                        <p className="gallery-list-meta">Vælg billeder</p>
                        <div className="admin-home-block-images__grid">
                            {images.map((image) => (
                                <label key={`edit-${image.id}`} className="checkbox-row">
                                    <input
                                        type="checkbox"
                                        checked={newBlockImageIds.includes(Number(image.id))}
                                        onChange={(event) =>
                                            setNewBlockImageIds((current) =>
                                                toggleBlockImage(Number(image.id), event.target.checked, current)
                                            )
                                        }
                                    />
                                    <span>{image.title || image.filename || `Billede ${image.id}`}</span>
                                </label>
                            ))}
                        </div>
                    </div>
                    <div className="modal-actions admin-home-block-form__actions">
                        <button
                            type="button"
                            onClick={() => {
                                setEditingBlock(null);
                                resetBlockForm();
                            }}
                        >
                            Annuller
                        </button>
                        <button type="submit">Gem</button>
                    </div>
                </form>
            </Modal>

        </div>
    );
};

export default Admin;
